/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Permission decision rules — pure, with no imports.
 *
 * Deliberately free of React and axios so it can be unit tested. Importing the
 * hook module instead pulls in Auth.js -> axios, which this project's Jest
 * setup cannot transform (the same reason the existing auth tests fail to run).
 *
 * Mirrors the backend, where the resolver is kept free of I/O for exactly this
 * reason: the logic deciding who sees what is the part most worth testing.
 */

const SUPPORT_ROLES = new Set(['SUPPORT', 'support']);

const LEVEL_RANK = { n: 0, r: 1, rw: 2, rwd: 3 };
const SCOPE_NAME = { o: 'own', t: 'team', tas: 'team_and_subteams', a: 'all' };

export function isSupportUser(user) {
  return Boolean(user?.is_support_user) || SUPPORT_ROLES.has(user?.role);
}

/**
 * Guard order is the safety-critical part:
 *
 *   1. no object name    -> legacy. Keeps all 70 existing call sites intact.
 *   2. mode !== enforce  -> legacy. Enforcement is opt-in per tenant and is
 *                           declared by the server, never inferred here.
 *   3. !ready            -> legacy. Never deny while the first fetch is in
 *                           flight; "not loaded" and "not permitted" must not
 *                           look the same to a user.
 *   4. isSuper           -> allow everything.
 *
 * A slow or failed permissions fetch therefore degrades to a more permissive UI,
 * never to a user staring at an empty CRM. The backend enforces regardless; the
 * frontend only hides affordances it knows are pointless.
 */
export function computePermission({
  user,
  objectApiName,
  snapshot,
  ready,
  mode,
  isSuper,
}) {
  const support = isSupportUser(user);

  // Support users are read-only whatever their grants say, mirroring
  // SupportUserRestrictionMiddleware, which blocks their writes server-side.
  const legacy = {
    canRead: true,
    canCreate: !support,
    canEdit: !support,
    canDelete: !support,
    isSupportUser: support,
    scope: 'all',
    ready: Boolean(ready),
    loading: !ready,
  };

  if (!objectApiName || mode !== 'enforce' || !ready) return legacy;
  if (isSuper) return { ...legacy, ready: true, loading: false };

  const grant = snapshot?.objects?.[objectApiName];
  const level = LEVEL_RANK[grant?.g ?? 'n'] ?? 0;
  const scope = SCOPE_NAME[grant?.s ?? 'o'] ?? 'own';

  return {
    canRead: level >= 1,
    canCreate: level >= 2 && !support,
    canEdit: level >= 2 && !support,
    canDelete: level >= 3 && !support,
    isSupportUser: support,
    scope,
    ready: true,
    loading: false,
  };
}

export function computeCapability({
  user,
  capability,
  capabilities,
  ready,
  mode,
  isSuper,
}) {
  if (mode !== 'enforce' || !ready) {
    // Lets capability checks be adopted in the UI before any tenant enforces.
    // Role may be a string or an enum-like { value } from the API.
    const role = String(user?.role?.value ?? user?.role ?? '').toLowerCase();
    return role === 'system_admin';
  }
  return Boolean(isSuper) || Boolean(capabilities?.has?.(capability));
}
